import { Link } from "react-router-dom";

interface MarkdownContentProps {
  content: string;
  className?: string;
}

const MarkdownContent = ({ content, className = "" }: MarkdownContentProps) => {
  if (!content) return null;

  const parseInlineContent = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    const regex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*)/g;
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      if (match[2] && match[3]) {
        const linkText = match[2];
        const url = match[3];
        const isExternal = url.startsWith("http://") || url.startsWith("https://");

        if (isExternal) {
          parts.push(
            <a
              key={keyIndex++}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              {linkText}
            </a>
          );
        } else {
          parts.push(
            <Link
              key={keyIndex++}
              to={url}
              className="text-primary hover:underline font-medium"
            >
              {linkText}
            </Link>
          );
        }
      } else if (match[4]) {
        parts.push(<strong key={keyIndex++}>{match[4]}</strong>);
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  const parseContent = (content: string): React.ReactNode[] => {
    const lines = content.split("\n");
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let keyIndex = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={keyIndex++} className="list-disc list-inside space-y-2 mb-6 text-muted-foreground">
            {currentList.map((item, i) => (
              <li key={i}>{parseInlineContent(item)}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Skip empty lines
      if (!line) {
        flushList();
        continue;
      }

      // Headings - use design system utilities for site-wide consistency
      if (line.startsWith("### ")) {
        flushList();
        elements.push(
          <h4 key={keyIndex++} className="heading-small text-foreground mt-5 mb-2 first:mt-0">
            {parseInlineContent(line.slice(4))}
          </h4>
        );
        continue;
      }

      if (line.startsWith("## ")) {
        flushList();
        elements.push(
          <h3 key={keyIndex++} className="heading-card text-foreground mt-6 mb-3 first:mt-0">
            {parseInlineContent(line.slice(3))}
          </h3>
        );
        continue;
      }

      if (line.startsWith("# ")) {
        flushList();
        elements.push(
          <h2 key={keyIndex++} className="heading-article text-foreground mt-8 mb-4 first:mt-0">
            {parseInlineContent(line.slice(2))}
          </h2>
        );
        continue;
      }

      // List items
      if (line.startsWith("- ") || line.startsWith("* ")) {
        currentList.push(line.slice(2));
        continue;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={keyIndex++} className="text-muted-foreground mb-4 leading-relaxed">
          {parseInlineContent(line)}
        </p>
      );
    }

    flushList();
    return elements;
  };

  return (
    <div className={`not-prose max-w-none [&>*:first-child]:mt-0 ${className}`}>
      {parseContent(content)}
    </div>
  );
};

export default MarkdownContent;
